import { estiloRotulo, formatterValor, montarSeries, opcoesBase } from "./base";
import { deveRotular } from "../tema";

/**
 * Família 1 — coluna agrupada com 2+ séries.
 *
 * É o modelo do exemplo que originou este gerador (Soja, média de preço
 * por bimestre, 2025 x 2026) e o mais usado do acervo: uma categoria no
 * eixo X, uma coluna por série, valor em caixa acima de cada coluna e
 * legenda de quadradinhos no topo.
 */
const modelo = {
  id: "coluna-agrupada",
  label: "Coluna agrupada",
  descricao: "Duas ou mais séries lado a lado por categoria. Ex.: 2025 x 2026 por bimestre.",

  build(spec) {
    const base = opcoesBase(spec);
    const totalCategorias = spec.dados?.categorias?.length || 0;
    const exibirEm = spec.valores?.exibirEm ?? "todos";
    // As referências usam colunas cilíndricas em perspectiva (é a cara do
    // gráfico 3D do Excel, de onde esse padrão veio). "chato" fica como
    // alternativa para quem quiser a coluna reta.
    const cilindro = (spec.extras?.formato ?? "cilindro") === "cilindro";

    return {
      ...base,
      chart: {
        ...base.chart,
        type: cilindro ? "cylinder" : "column",
        ...(cilindro
          ? {
              options3d: {
                enabled: true,
                alpha: 12,
                beta: 0,
                depth: 42,
                viewDistance: 30,
                // Só o "chão" fica visível, como o piso cinza do gráfico 3D
                // do Excel que as referências reproduzem. Parede de fundo e
                // lateral desligadas — senão viram um caixote branco.
                frame: {
                  bottom: { size: 1, color: "rgba(255,255,255,0.30)" },
                  back: { size: 0, color: "transparent" },
                  side: { size: 0, color: "transparent" },
                  top: { size: 0, color: "transparent" },
                },
              },
            }
          : {}),
      },
      xAxis: {
        ...base.xAxis,
        categories: spec.dados?.categorias || [],
      },
      plotOptions: {
        ...base.plotOptions,
        series: {
          ...base.plotOptions.series,
          borderWidth: 0,
          // Colunas largas e quase encostadas dentro do grupo, como nas
          // referências. O padding negativo é o que permite passar da
          // fatia nominal de cada ponto.
          groupPadding: 0.03,
          pointPadding: -0.04,
          grouping: true,
          depth: 42,
          groupZPadding: 0,
          dataLabels: {
            ...estiloRotulo(spec),
            formatter: formatterValor(spec),
          },
        },
      },
      series: montarSeries(spec).map((serie) => ({
        ...serie,
        // O rótulo por ponto permite o padrão "só as pontas" das séries
        // longas sem precisar de um modelo separado.
        data: serie.data.map((valor, i) => ({
          y: valor,
          dataLabels: { enabled: deveRotular(i, totalCategorias, exibirEm) },
        })),
      })),
    };
  },

  /** Dados de exemplo — os do gráfico de referência desta família. */
  exemplo() {
    return {
      modelo: "coluna-agrupada",
      titulo: [
        { texto: "Soja", cor: "#F0F000" },
        { texto: " - Média de Preço Brasil ", cor: "#FFFFFF" },
        { texto: "(US$/sc)", cor: "#FFFFFF", tamanho: 10 },
      ],
      subtitulo: [],
      fonte: "Fonte:  IPAR RURAL BUSINESS",
      fundo: "#000000",
      dados: {
        categorias: ["1ºbim", "2ºbim", "3ºbim", "4ºbim"],
        series: [
          { nome: "2025", cor: "#FFFF0B", corFim: "#7D8015", valores: [21.1, 21.3, 21.9, 23.5] },
          { nome: "2026", cor: "#3DAA73", corFim: "#216443", valores: [22.6, 22.6, 23.4, 26.0] },
        ],
      },
      legenda: { visivel: true },
      valores: { decimais: 1, prefixo: "", sufixo: "", exibirEm: "todos" },
      rotulo: { corTexto: "#FFFFFF", corFundo: "#1A1A1A", corBorda: "#FFFFFF" },
      extras: {},
    };
  },
};

export default modelo;
